import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUnreadNotificationCount } from '../services/api';
import BrandMark from './BrandMark';
import './Navbar.css';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem('theme') === 'dark'
  );

  useEffect(() => {
    if (!user) return undefined;
    const load = () => {
      getUnreadNotificationCount()
        .then((res) => setUnreadCount(res.data.count || 0))
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const approvalsLabel = user?.role === 'manager' ? 'Approvals' : 'Requests';

  if (!user) {
    return (
      <header className="topbar">
        <Link to="/" className="topbar-brand">
          <BrandMark size={32} />
          <span>
            Access Manager
            <small>Repository access</small>
          </span>
        </Link>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? 'Light' : 'Dark'}
          </button>
          <Link to="/login" className="btn btn-primary btn-sm">Sign in</Link>
        </div>
      </header>
    );
  }

  return (
    <>
      <header className="shell-top">
        <button className="btn btn-secondary btn-sm" type="button" onClick={() => setMenuOpen(true)}>
          Menu
        </button>
        <span className="shell-brand" style={{ pointerEvents: 'none' }}>
          <BrandMark size={28} />
          Access Manager
        </span>
      </header>
      {menuOpen && (
        <button className="shell-backdrop" type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)} />
      )}
      <aside className={`shell-nav${menuOpen ? ' is-open' : ''}`}>
        <Link to="/dashboard" className="shell-brand">
          <BrandMark size={32} />
          <span>
            Access Manager
            <small>Repository access</small>
          </span>
        </Link>

        <p className="shell-group">Home</p>
        <NavLink to="/dashboard" className={({ isActive }) => `shell-link${isActive ? ' active' : ''}`}>
          Dashboard
        </NavLink>
        <NavLink to="/repositories" className={({ isActive }) => `shell-link${isActive ? ' active' : ''}`}>
          Repositories
        </NavLink>

        <p className="shell-group">Decide</p>
        <NavLink to="/access-requests" className={({ isActive }) => `shell-link${isActive ? ' active' : ''}`}>
          {approvalsLabel}
        </NavLink>
        <NavLink to="/access-reviews" className={({ isActive }) => `shell-link${isActive ? ' active' : ''}`}>
          Reviews
        </NavLink>
        {user.role === 'admin' && (
          <NavLink to="/admin" className={({ isActive }) => `shell-link${isActive ? ' active' : ''}`}>
            Admin
          </NavLink>
        )}

        <div className="shell-spacer" />

        <NavLink to="/notifications" className={({ isActive }) => `shell-link${isActive ? ' active' : ''}`}>
          Notifications
          {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => `shell-link${isActive ? ' active' : ''}`}>
          Settings
        </NavLink>
        <button className="btn btn-secondary theme-toggle" type="button" onClick={() => setDarkMode(!darkMode)}>
          {darkMode ? 'Use light appearance' : 'Use dark appearance'}
        </button>

        <div className="shell-user">
          <div className="shell-user-row">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="navbar-avatar" />
            ) : (
              <span className="navbar-avatar" />
            )}
            <div>
              <div className="shell-user-name">{user.displayName || user.username}</div>
              <div className="shell-user-role">{user.role || 'user'}</div>
            </div>
          </div>
          <button type="button" onClick={logout} className="btn btn-secondary btn-sm">
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
};

export default Navbar;
